import { WebSocketServer, WebSocket } from 'ws';
import type {
  WSMessage,
  WSComponentSelectedMessage,
  WSChatMessage,
  WSConnectionMessage,
  WSErrorMessage,
} from '@hotshot-it/shared';

export interface WebSocketServerOptions {
  port: number;
  server?: any; // ViteDevServer's httpServer type
}

export class HotshotWebSocketServer {
  private wss: WebSocketServer;
  private clients = new Set<WebSocket>();

  constructor(options: WebSocketServerOptions) {
    // Use noServer mode and attach to Vite's HTTP server
    this.wss = new WebSocketServer({
      noServer: true,
    });

    // Handle upgrade requests on Vite's HTTP server
    if (options.server) {
      options.server.on('upgrade', (request: any, socket: any, head: any) => {
        // Only handle WebSocket upgrades for Hotshot
        const pathname = new URL(request.url, 'http://localhost').pathname;
        if (pathname === '/hotshot-ws') {
          this.wss.handleUpgrade(request, socket, head, (ws) => {
            this.wss.emit('connection', ws, request);
          });
        }
      });
    }

    this.wss.on('connection', this.handleConnection.bind(this));
  }

  private handleConnection(ws: WebSocket) {
    console.log('[Hotshot] Client connected');
    this.clients.add(ws);

    // Send connection ready message
    const message: WSConnectionMessage = {
      type: 'connection:ready',
      serverVersion: '0.0.1',
    };
    ws.send(JSON.stringify(message));

    ws.on('message', (data: Buffer) => {
      try {
        const message = JSON.parse(data.toString()) as WSMessage;
        this.handleMessage(ws, message);
      } catch (error) {
        this.sendError(ws, 'Invalid message format', error);
      }
    });

    ws.on('close', () => {
      console.log('[Hotshot] Client disconnected');
      this.clients.delete(ws);
    });

    ws.on('error', (error) => {
      console.error('[Hotshot] WebSocket error:', error);
      this.clients.delete(ws);
    });
  }

  private handleMessage(ws: WebSocket, message: WSMessage) {
    // Delegate to plugin handler (will be set up in index.ts)
    this.onMessage?.(ws, message);
  }

  public onMessage?: (ws: WebSocket, message: WSMessage) => void;

  public sendToClient(ws: WebSocket, message: WSMessage) {
    if (ws.readyState === WebSocket.OPEN) {
      ws.send(JSON.stringify(message));
    }
  }

  public broadcast(message: any) {
    const payload = typeof message === 'string' ? message : JSON.stringify(message);
    this.clients.forEach((client) => {
      if (client.readyState === WebSocket.OPEN) {
        client.send(payload);
      }
    });
  }

  public sendError(ws: WebSocket, error: string, details?: unknown) {
    const message: WSErrorMessage = {
      type: 'error',
      error,
      details,
    };
    this.sendToClient(ws, message);
  }

  public close() {
    this.clients.forEach((client) => client.close());
    this.wss.close();
  }
}
